import * as React from 'react';
import { observer } from 'mobx-react';
import { Table, IconButton, Button, Modal, Input, FormPanel, SubmitButton, CreateButton } from '@ixicare/ui';
import type { ZonePresetEntry } from '@ixicare/resources';
import { SquarePen, Trash2 } from 'lucide-react';
import { t } from '~/i18n';
import { useStores } from '~store/utils';
import { useZonePresets, useZonePresetMutations } from '~modules/zone-presets/useZonePresets';
import GeofenceCreatorContainer from '~modules/geofencing/container/GeofenceCreatorContainer';
import modalStyles from '~modules/geofencing/container/geofenceEditorModal.module.css';

export const ZonePresetsTab: React.FC = observer(() => {
  const { sites, confirmation } = useStores();
  const site = sites.selectedSite;

  const { data: presets } = useZonePresets(site?.id);
  const { create, update, remove } = useZonePresetMutations(site?.id ?? 0);

  const [renaming, setRenaming] = React.useState<ZonePresetEntry | null>(null);
  const [editing, setEditing] = React.useState<ZonePresetEntry | null>(null);
  // Carried across the confirmation so a cancel puts the carer back in the editor with their work,
  // rather than throwing away a selection they may have spent a while on.
  const [pendingZoneIds, setPendingZoneIds] = React.useState<string[] | null>(null);
  const [isCreating, setCreating] = React.useState(false);
  const [namingZoneIds, setNamingZoneIds] = React.useState<string[] | null>(null);

  const openEditor = (preset: ZonePresetEntry) => {
    setPendingZoneIds(null);
    setEditing(preset);
  };

  const confirmZones = (preset: ZonePresetEntry, zoneIds: string[]) => {
    setEditing(null);
    setPendingZoneIds(zoneIds);
    confirmation.show({
      title: t('zonePresets.updateZones.confirmTitle', { name: preset.name }),
      message: t('zonePresets.updateZones.confirmMessage', {
        groups: preset.hcpGroupCount,
        senders: preset.profileCount,
      }),
      confirmLabel: t('zonePresets.updateZones.confirm'),
      dangerous: true,
      onConfirm: () => {
        setPendingZoneIds(null);
        update.mutate({ presetId: preset.id, zoneIds });
      },
      onCancel: () => setEditing(preset),
    });
  };

  const finishCreating = (name: string) => {
    if (!namingZoneIds) return;
    create.mutate({ name, zoneIds: namingZoneIds });
    setNamingZoneIds(null);
  };

  const confirmDelete = (preset: ZonePresetEntry) => {
    const followers = preset.hcpGroupCount + preset.profileCount;
    confirmation.show({
      title: t('zonePresets.delete.confirmTitle', { name: preset.name }),
      message: followers
        ? t('zonePresets.delete.confirmMessage', {
            groups: preset.hcpGroupCount,
            senders: preset.profileCount,
          })
        : t('zonePresets.delete.confirmUnused'),
      confirmLabel: t('zonePresets.delete.confirm'),
      dangerous: true,
      onConfirm: () => remove.mutate(preset.id),
    });
  };

  return (
    <>
      <Table>
        <Table.Head>
          <Table.Row>
            <Table.HeadCol>{t('zonePresets.table.name')}</Table.HeadCol>
            <Table.HeadCol>{t('zonePresets.table.zones')}</Table.HeadCol>
            <Table.HeadCol>{t('zonePresets.table.receiverGroups')}</Table.HeadCol>
            <Table.HeadCol>{t('zonePresets.table.alarmSenders')}</Table.HeadCol>
            {/* Action columns; the icons carry `title` tooltips. */}
            <Table.HeadCol>{''}</Table.HeadCol>
            <Table.HeadCol>{''}</Table.HeadCol>
          </Table.Row>
        </Table.Head>
        <Table.Body>
          {presets?.map((preset) => (
            <Table.Row key={preset.id}>
              <Table.Col>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  {preset.name}
                  <span title={t('zonePresets.rename.title')}>
                    <IconButton lucide={SquarePen} onClick={() => setRenaming(preset)} />
                  </span>
                </div>
              </Table.Col>
              <Table.Col>
                {preset.zones.length ? preset.zones.map((zone) => zone.name).join(', ') : t('zonePresets.noZones')}
              </Table.Col>
              <Table.Col>{preset.hcpGroupCount}</Table.Col>
              <Table.Col>{preset.profileCount}</Table.Col>
              <Table.Col>
                <Button onClick={() => openEditor(preset)}>{t('zonePresets.editZones.button')}</Button>
              </Table.Col>
              <Table.Col>
                <span title={t('residents.table.delete')}>
                  <IconButton lucide={Trash2} onClick={() => confirmDelete(preset)} />
                </span>
              </Table.Col>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
      {presets?.length === 0 && <p>{t('zonePresets.empty')}</p>}

      <CreateButton atBottom onClick={() => setCreating(true)} />

      {isCreating && site && (
        <Modal onClose={() => setCreating(false)} className={modalStyles.wide}>
          <h3 className="text-center mb-30">{t('zonePresets.create.title')}</h3>
          <GeofenceCreatorContainer
            siteUuid={site.uuid}
            subject="preset"
            selectedZones={[]}
            onSave={(selection) => {
              setCreating(false);
              setNamingZoneIds(selection.zoneIds);
            }}
            onCancel={() => setCreating(false)}
          />
        </Modal>
      )}

      {namingZoneIds && (
        <PresetNameModal
          title={t('geofence.preset.name.title')}
          initialName=""
          isPending={create.isPending}
          onSubmit={finishCreating}
          // Back to the picker with the zones intact, rather than losing them to a stray close.
          onClose={() => {
            setNamingZoneIds(null);
            setPendingZoneIds(namingZoneIds);
            setCreating(true);
          }}
        />
      )}

      {renaming && (
        <PresetNameModal
          title={t('zonePresets.rename.title')}
          initialName={renaming.name}
          isPending={update.isPending}
          onSubmit={(name) => {
            if (name !== renaming.name) update.mutate({ presetId: renaming.id, name });
            setRenaming(null);
          }}
          onClose={() => setRenaming(null)}
        />
      )}

      {editing && site && (
        <Modal onClose={() => setEditing(null)} className={modalStyles.wide}>
          <h3 className="text-center mb-30">{t('zonePresets.editZones.title', { name: editing.name })}</h3>
          <GeofenceCreatorContainer
            siteUuid={site.uuid}
            // Deliberately no `siteId`: that is what turns the preset machinery off. Here the preset
            // *is* what is being edited, so there is nothing to follow and nothing to save as new -
            // offering a picker of other presets would only ask which one to abandon this for.
            subject="preset"
            selectedZones={pendingZoneIds ?? editing.zones.map((zone) => zone.uuid)}
            onSave={(selection) => confirmZones(editing, selection.zoneIds)}
            onCancel={() => setEditing(null)}
          />
        </Modal>
      )}
    </>
  );
});

function PresetNameModal({
  title,
  initialName,
  isPending,
  onSubmit,
  onClose,
}: {
  title: string;
  initialName: string;
  isPending: boolean;
  onSubmit: (name: string) => void;
  onClose: () => void;
}) {
  const [name, setName] = React.useState(initialName);

  const handleSubmit = (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      onClose();
      return;
    }
    onSubmit(trimmed);
  };

  return (
    <Modal onClose={onClose} style={{ maxWidth: 450 }}>
      <h3 className="text-center mb-30">{title}</h3>
      <FormPanel>
        <form onSubmit={handleSubmit}>
          <Input
            label={t('zonePresets.rename.label')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            autoFocus
          />
          <div className="d-flex justify-content-end mt-30">
            <SubmitButton type="submit" loading={isPending}>
              {t('global.submit')}
            </SubmitButton>
          </div>
        </form>
      </FormPanel>
    </Modal>
  );
}

export default ZonePresetsTab;
